import { DeleteOutlined } from "@ant-design/icons";
import { Conversations } from "@ant-design/x";
import type { ConversationItemType, ConversationsProps } from "@ant-design/x";
import { DateUtil } from "@dto/util/DateUtil";
import { Empty, Modal } from "antd";
import dayjs from "dayjs";
import { FC, useContext, useEffect, useMemo } from "react";
import { Label } from "src/data/Label";
import { ChatContext } from "./AIChat";
import { loadPyxbotHistory, removePyxbotHistory } from "./chatService";
import { signedInHistoryOwner } from "./historyOwner";

function conversationGroup(updated: string) {
  const day = dayjs(updated);
  if (!day.isValid()) {
    return "Earlier";
  }
  const days = dayjs().startOf("day").diff(day.startOf("day"), "day");
  if (days <= 0) {
    return "Today";
  }
  if (days === 1) {
    return "Yesterday";
  }
  if (days < 7) {
    return "Previous 7 days";
  }
  if (days < 30) {
    return "Previous 30 days";
  }
  return "Earlier";
}

export const AIChatHistory: FC = () => {
  const { history, setHistory, conversation, setConversation, historyOpen, setHistoryOpen } = useContext(ChatContext);
  const [modal, modalHolder] = Modal.useModal();

  useEffect(() => {
    if (!historyOpen) {
      return;
    }
    setHistory(loadPyxbotHistory(signedInHistoryOwner()));
  }, [historyOpen, setHistory]);

  function deleteConversation(localId: string) {
    setHistory((prev) => removePyxbotHistory(prev, localId, signedInHistoryOwner()));
    if (conversation?.localId === localId) {
      setConversation(undefined);
    }
  }

  const items = useMemo<ConversationItemType[]>(() => history.map((lh) => ({
    key: lh.localId,
    label: lh.topic,
    title: `${lh.topic} - ${DateUtil.getShortDateTime(lh.updated)}`,
    group: conversationGroup(lh.updated),
  })), [history]);

  const onActiveChange: ConversationsProps["onActiveChange"] = (key) => {
    const lh = history.find((h) => h.localId === key);
    if (lh) {
      setConversation(lh);
      setHistoryOpen(false);
    }
  };

  const menu: ConversationsProps["menu"] = (item) => ({
    items: [{ key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true }],
    onClick: ({ key, domEvent }) => {
      domEvent?.stopPropagation();
      if (key !== "delete") {
        return;
      }
      const lh = history.find((h) => h.localId === item.key);
      if (!lh) {
        return;
      }
      void modal.confirm({
        title: "Delete conversation",
        content: <span>Delete <strong>{lh.topic}</strong> conversation?</span>,
        okText: "Delete",
        okButtonProps: { danger: true },
        onOk: () => deleteConversation(lh.localId),
      });
    },
  });

  return <Modal className="xm-chat-history"
    open={historyOpen} title={Label.XORBOT + " Chat History"} onCancel={() => setHistoryOpen(false)}
    cancelText="Close" okButtonProps={{ hidden: true }}>
    {modalHolder}
    {items.length
      ? <Conversations items={items} groupable menu={menu} activeKey={conversation?.localId} onActiveChange={onActiveChange} />
      : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No conversations yet" />}
  </Modal>;
};
